import { useEffect, useMemo, useRef, useState } from "react"

import { Flex, Tooltip, Typography } from "antd"
import cls from "classname"

import Private from "../../../../Components/Private"
import { titleSortFilters } from "../../../../Components/titleSortFilters"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import { cardBrandToTextMap } from "../../utils/cardOptions"
import { formatAmount, formatCount } from "./columns"

import "./byReasonCodeStyles.less"

const { Text } = Typography

export const getColumnsByReasonCode = ({
  t,
  withSort,
  theme,
  expandedOtherKeys,
  otherExpanded
} = {}) => {
  return [
    {
      key: "reason_code",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Reason code"),
          sorter: withSort,
          stateData: state,
          key: "reason_code"
        }),
      dataIndex: "reason_code",
      width: 300,
      render: (_, record) => {
        let image = Images?.[record?.card_brand?.toLowerCase()] || null
        const isOtherRow = record?.isOtherRow
        const isOtherCardTypeRow = record?.isOtherCardTypeRow
        const isOtherReasonCodeTypeRow = record?.isOtherReasonCodeTypeRow
        const isExpandable = (isOtherRow && record?.card_types?.length > 0) || isOtherCardTypeRow
        const isExpanded =
          expandedOtherKeys?.includes(record?.reason_code) || (isOtherRow && otherExpanded)

        let text = _
        if (isOtherRow) text = "Other"
        if (isOtherCardTypeRow)
          text = cardBrandToTextMap[record?.card_type?.toLowerCase()] || record?.card_type
        if (isOtherReasonCodeTypeRow) text = ""
        if (isOtherCardTypeRow) image = Images?.[record?.card_type?.toLowerCase()] || null
        let marginLeft = 0
        if (isOtherCardTypeRow) marginLeft = 16
        if (isOtherReasonCodeTypeRow) marginLeft = 48
        return (
          <Flex gap={8} align="flex-start" style={{ maxWidth: "300px", marginLeft }}>
            {image && <img src={image} alt={text} width={20} />}
            <Flex gap={2} vertical style={{ overflow: "hidden" }}>
              <Text strong>{text}</Text>
              <ReasonCodeDescription description={record?.tooltip || []} />
            </Flex>
            {isExpandable && (
              <Icon
                type="chevron_down_small"
                className={cls("chevron_down", { expanded: isExpanded })}
              />
            )}
          </Flex>
        )
      }
    },
    {
      key: "type",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Type"),
          sorter: withSort,
          stateData: state,
          key: "type"
        }),
      dataIndex: "type",
      className: "type-column",
      render: (_, record) => {
        let text = _
        if (record?.isOtherReasonCodeTypeRow)
          text = record?.dispute_type?.[0]?.toUpperCase() + record?.dispute_type?.slice(1)

        if (record?.isOtherRow) text = "Other"
        return <Text strong>{text}</Text>
      }
    },
    {
      key: "chargebacks",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Chargebacks"),
          sorter: withSort,
          stateData: state,
          key: "chargebacks_count",
          justify: "end"
        }),
      dataIndex: "chargebacks_count",
      align: "right",
      render: (_, record) => (
        <Flex gap={2} vertical={true}>
          <Text strong>{formatCount(record?.["chargebacks_count"])}</Text>
          <Text type="secondary" strong>
            <Private>{formatAmount(record?.["chargebacks_amount"])}</Private>
          </Text>
        </Flex>
      )
    },
    {
      key: "chargebacks_without_us",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Chargebacks without us"),
          sorter: withSort,
          stateData: state,
          key: "chargebacks_count_without_us",
          justify: "end"
        }),
      dataIndex: "chargebacks_count_without_us",
      align: "right",
      render: (_, record) => (
        <Flex gap={2} vertical={true}>
          <Text strong>{formatCount(record?.["chargebacks_count_without_us"])}</Text>
          <Text type="secondary" strong>
            <Private>{formatAmount(record?.["chargebacks_amount_without_us"])}</Private>
          </Text>
        </Flex>
      )
    },
    {
      key: "alerts",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Alerts"),
          sorter: withSort,
          stateData: state,
          key: "alerts_count",
          justify: "end"
        }),
      dataIndex: "alerts_count",
      align: "right",
      render: (_, record) => (
        <Flex gap={2} vertical={true}>
          <Text strong>{formatCount(record?.["alerts_count"])}</Text>
          <Text type="secondary" strong>
            <Private>{formatAmount(record?.["alerts_amount"])}</Private>
          </Text>
        </Flex>
      )
    },
    {
      key: "prevention_rate",
      sorter: withSort,
      title: (state) =>
        titleSortFilters({
          title: t("Prevention rate"),
          sorter: withSort,
          stateData: state,
          key: "prevention_rate",
          justify: "end"
        }),
      dataIndex: "prevention_rate",
      align: "right",
      render: (_, record) => <Text strong>{_}%</Text>
    }
  ]
}

export const ReasonCodeDescription = ({ description }) => {
  const ref = useRef(null)
  const [isOverflowed, setIsOverflowed] = useState(false)
  const isTooltipShown = isOverflowed || description?.length > 1

  const checkOverflow = () => {
    const el = ref.current
    if (el) {
      setIsOverflowed(el.scrollWidth > el.clientWidth)
    }
  }

  useEffect(() => {
    checkOverflow()

    window.addEventListener("resize", checkOverflow)
    return () => {
      window.removeEventListener("resize", checkOverflow)
    }
  }, [description])

  const tooltipTitle = useMemo(() => {
    if (!isTooltipShown) return ""
    if (description?.length === 1) return description[0]
    if (description?.length > 1)
      return (
        <Flex gap={4} vertical>
          {description.map((line, index) => (
            <Flex key={index} gap={6}>
              <div>•</div>
              <div>{line}</div>
            </Flex>
          ))}
        </Flex>
      )
  }, [description, isTooltipShown])

  if (!description.length) return null

  return (
    <Tooltip title={tooltipTitle} placement="topLeft">
      <Text
        className={cls("reason-code-description", {
          "with-tooltip": isTooltipShown
        })}
        type="secondary"
        ref={ref}
      >
        {description[0] + (description.length > 1 && !isOverflowed ? "..." : "")}
      </Text>
    </Tooltip>
  )
}
